import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Calendar, Building2, CreditCard, Database, Lock, Mail, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageSeo } from '@/components/seo/PageSeo';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language;

  // Privacy policy content in multiple languages
  const content = {
    de: {
      title: "DATENSCHUTZHINWEIS",
      effectiveDate: "Gültig ab: 10 Januar 2026",
      intro: {
        title: "Einführung",
        text: `Wir sind verpflichtet Ihre Privatsphäre und Ihre personenbezogenen Daten in Übereinstimmung mit der Datenschutzverordnung der Europäischen Union ("DSGVO") sowie anderen geltenden Datenschutzgesetzen zu schützen. In diesem Datenschutzhinweis wird erläutert, wie wir Ihre personenbezogenen Daten erheben, verwenden, weitergeben und schützen, wenn Sie den von uns angebotenen Dienst zum Speichern Ihrer Kredit-/Debitkartendaten auf der Plattform (mobile App oder Website auf mobilem/Desktop-Gerät) nutzen.

Die Speicherung Ihrer Kredit-/Debitkartendaten ist freiwillig. Mit Ihrem Einverständnis speichern wir sie in unserem System, um Ihr Shopping-Erlebnis zu verbessern und damit Sie sie bei zukünftigen Einkäufen und Zahlungen wiederverwenden können. Sie können auch shoppen, ohne Ihre Karte zu registrieren, und ob Sie Ihre Kartendaten speichern möchten, liegt ganz bei Ihnen. Ihre gespeicherten Kartendaten können Sie jederzeit von der Plattform löschen.`
      },
      controller: {
        title: "DATENVERANTWORTLICHE",
        text: `E-Market Germany UG (oder „Unternehmen") sind die Datenverantwortlichen für personenbezogene Daten, die im EWR, im Vereinigten Königreich und in der Schweiz erfasst und verwendet werden.`,
        contact: `E-Market Germany UG
Amtsgericht Köln HRB 106542
Thessaloniki Allee 12, 51103 Köln, Deutschland`
      },
      section1: {
        title: "1. Welche Daten erfasst E-Market Germany UG (Vertreter von Turkishemarket.com)",
        text: `Karteninformationen: Wenn Sie Ihre Kartendaten speichern, indem Sie das Kontrollkästchen für die Speicherung von Kartendaten aktivieren oder indem Sie die entsprechende Option auswählen und Ihre Einwilligung geben, erfassen wir Ihre Kartendaten, genauer die ersten 6 und die letzten 4 Ziffern, das Ablaufdatum, die ausstellende Bank, Informationen zum Kartensystem sowie das Datum, an dem die Karte im System registriert wurde.

E-Market Germany UG (Vertreter von Turkishemarket.com) verarbeitet die oben genannten personenbezogenen Daten nur auf Grundlage der ausdrücklichen Einwilligung.

Um Datensicherheit zu gewährleisten, werden Ihre Kartendaten von den Unternehmen gemäß den Anforderungen des PCI DSS Level 1 gespeichert.

Bitte beachten Sie, dass Ihre gespeicherte Karte mit der Telefonnummer verknüpft wird, die Sie bei Ihrer Bestellung angegeben haben. Um die Telefonnummer zu ändern, die mit dieser Kredit-/Debitkarte verknüpft ist, müssen Sie Ihre registrierte Karte löschen und sie zusammen mit der gewünschten Telefonnummer erneut hinzufügen. Wenn Sie Ihre Karte auf der Plattform speichern, können Sie sie für Einkäufe auf der Plattform in unterschiedlichen Ländern verwenden.`
      },
      section2: {
        title: "2. Wie verwendet E-Market Germany UG (Vertreter von Turkishemarket.com) Ihre Daten?",
        text: "Die Unternehmen verarbeiten Ihre Daten zu folgenden Zwecken:",
        items: [
          "Zur Verbesserung Ihres Shopping-Erlebnisses",
          "Zur Optimierung Ihres Zahlungsprozesses",
          "Damit Sie sie für zukünftige Käufe und Zahlungen wiederverwenden können",
          "Um Anfragen und Beschwerden nachzugehen",
          "Als Beweismittel in etwaigen zukünftigen Streitfällen"
        ]
      },
      section3: {
        title: "3. An wen gibt E-Market Germany UG (Vertreter von Turkishemarket.com) Daten weiter?",
        text: "Die Unternehmen geben grundsätzlich keine Kartendaten an Dritte weiter. Auf Anfrage oder im Falle eines möglichen Rechtsstreits können sie jedoch zur Erfüllung unserer eigenen rechtlichen Verpflichtungen gemäß den gesetzlichen Bestimmungen an autorisierte Institutionen und Organisationen oder Justizbehörden weitergegeben werden."
      },
      section4: {
        title: "4. Aufbewahrung von Daten",
        text: `Wir erfassen und verwenden personenbezogene Daten für die oben aufgeführten Zwecke. Je nach Zweck unterscheiden sich die Anforderungen und Aufbewahrungsfristen. Wir löschen Ihre personenbezogenen Daten, sobald sie nicht mehr für den Zweck benötigt werden, für den sie erhoben wurden.

Anträge auf Löschung personenbezogener Daten werden für gewöhnlich innerhalb von 30 Tagen bearbeitet. Unter bestimmten Bedingungen kann es auch länger dauern. In diesem Fall werden wir Sie entsprechend informieren. Bitte beachten Sie, dass wir unter Umständen gesetzlich dazu verpflichtet sind, bestimmte personenbezogene Daten aufzubewahren, um unseren eigenen gesetzlichen Verpflichtungen nachzukommen.`
      },
      section5: {
        title: "5. Ihre Rechte",
        text: "Als betroffene Person haben Sie gemäß der DSGVO die folgenden Rechte:",
        items: [
          "Recht auf Zugang: Sie haben das Recht, Zugang zu den personenbezogenen Daten zu erhalten, die wir zu Ihnen erfasst haben, und eine Kopie davon zu erhalten.",
          "Recht auf Berichtigung: Sie haben das Recht, fehlerhafte oder unvollständige personenbezogene Daten korrigieren zu lassen.",
          "Recht auf Löschung: Sie haben das Recht, unter bestimmten Bedingungen die Löschung Ihrer personenbezogenen Daten zu verlangen.",
          "Recht auf Einschränkung der Verarbeitung: Sie haben das Recht, unter bestimmten Bedingungen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen.",
          "Recht auf Datenübertragbarkeit: Sie haben das Recht, die zu Ihnen erfassten personenbezogenen Daten in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten.",
          "Recht auf Widerspruch: Sie haben das Recht, unter bestimmten Bedingungen der Verarbeitung Ihrer personenbezogenen Daten zu widersprechen.",
          "Recht auf Widerruf der Einwilligung: Wenn wir uns auf Ihre Einwilligung zur Verarbeitung Ihrer personenbezogenen Daten verlassen, haben Sie das Recht, diese Einwilligung jederzeit zu widerrufen."
        ]
      },
      section6: {
        title: "6. Kontakt",
        text: "Sollten Sie Fragen oder Anmerkungen dazu haben, wie wir Ihre personenbezogenen Daten verwenden, können Sie sich jederzeit per E-Mail an info@turkishemarket.com an unseren Datenschutzbeauftragten wenden."
      },
      section7: {
        title: "7. Recht auf das Einreichen einer Beschwerde bei einer Datenschutzbehörde",
        text: "Falls Sie der Ansicht sind, dass wir Ihre Anfrage oder Frage nicht angemessen beantwortet haben, haben Sie das Recht, bei der für Ihren Wohnort (das Land) zuständigen Aufsichtsbehörde eine Beschwerde einzureichen."
      }
    },
    tr: {
      title: "VERİ KORUMA BİLDİRİMİ",
      effectiveDate: "Yürürlük tarihi: 10 Ocak 2026",
      intro: {
        title: "Giriş",
        text: `Avrupa Birliği Genel Veri Koruma Tüzüğü ("GDPR") ve diğer geçerli veri koruma yasalarına uygun olarak gizliliğinizi ve kişisel verilerinizi korumaya kararlıyız. Bu Veri Koruma Bildirimi, platformda (mobil uygulama veya mobil/masaüstü cihazda web sitesi) kredi/banka kartı bilgilerinizi saklama hizmetimizi kullandığınızda kişisel verilerinizi nasıl topladığımızı, kullandığımızı, paylaştığımızı ve koruduğumuzu açıklamaktadır.

Kredi/banka kartı bilgilerinizin saklanması isteğe bağlıdır. Onayınızla, alışveriş deneyiminizi geliştirmek ve gelecekteki alışverişlerde ve ödemelerde yeniden kullanabilmeniz için bunları sistemimizde saklarız. Kartınızı kaydetmeden de alışveriş yapabilirsiniz ve kart bilgilerinizi saklamak tamamen size bağlıdır. Kayıtlı kart bilgilerinizi istediğiniz zaman platformdan silebilirsiniz.`
      },
      controller: {
        title: "VERİ SORUMLUSU",
        text: `E-Market Germany UG (veya "Şirket"), AEA, Birleşik Krallık ve İsviçre'de toplanan ve kullanılan kişisel veriler için veri sorumlusudur.`,
        contact: `E-Market Germany UG
Köln Ticaret Sicili HRB 106542
Thessaloniki Allee 12, 51103 Köln, Almanya`
      },
      section1: {
        title: "1. E-Market Germany UG (Turkishemarket.com'un temsilcisi) Hangi Verileri Toplar",
        text: `Kart Bilgileri: Kart bilgilerini saklama onay kutusunu işaretleyerek veya ilgili seçeneği seçerek ve onay vererek kart bilgilerinizi kaydettiğinizde, kart bilgilerinizi toplarız; özellikle ilk 6 ve son 4 hane, son kullanma tarihi, kartı veren banka, kart sistemi bilgileri ve kartın sisteme kayıt tarihi.

E-Market Germany UG (Turkishemarket.com'un temsilcisi), yukarıda belirtilen kişisel verileri yalnızca açık rıza temelinde işler.

Veri güvenliğini sağlamak için kart bilgileriniz şirketler tarafından PCI DSS Seviye 1 gereksinimlerine uygun olarak saklanır.

Lütfen kayıtlı kartınızın siparişinizde belirttiğiniz telefon numarasıyla ilişkilendirileceğini unutmayın. Bu kredi/banka kartıyla ilişkili telefon numarasını değiştirmek için kayıtlı kartınızı silmeniz ve istenen telefon numarasıyla birlikte yeniden eklemeniz gerekir. Kartınızı platformda kaydettiğinizde, farklı ülkelerdeki platform alışverişlerinde kullanabilirsiniz.`
      },
      section2: {
        title: "2. E-Market Germany UG (Turkishemarket.com'un temsilcisi) Verilerinizi Nasıl Kullanır?",
        text: "Şirketler verilerinizi aşağıdaki amaçlarla işler:",
        items: [
          "Alışveriş deneyiminizi geliştirmek için",
          "Ödeme sürecinizi optimize etmek için",
          "Gelecekteki alışverişlerde ve ödemelerde yeniden kullanabilmeniz için",
          "Talep ve şikayetleri takip etmek için",
          "Olası gelecekteki anlaşmazlıklarda kanıt olarak"
        ]
      },
      section3: {
        title: "3. E-Market Germany UG (Turkishemarket.com'un temsilcisi) Verileri Kimlerle Paylaşır?",
        text: "Şirketler prensip olarak kart bilgilerini üçüncü taraflarla paylaşmaz. Ancak talep üzerine veya olası bir hukuki anlaşmazlık durumunda, kendi yasal yükümlülüklerimizi yerine getirmek için yasal düzenlemelere uygun olarak yetkili kurum ve kuruluşlara veya yargı makamlarına iletilebilir."
      },
      section4: {
        title: "4. Verilerin Saklanması",
        text: `Yukarıda belirtilen amaçlar için kişisel verileri toplar ve kullanırız. Amaca bağlı olarak gereksinimler ve saklama süreleri farklılık gösterir. Kişisel verilerinizi toplandıkları amaç için artık gerekli olmadığında sileriz.

Kişisel verilerin silinmesi talepleri genellikle 30 gün içinde işlenir. Belirli koşullar altında daha uzun sürebilir; bu durumda sizi bilgilendireceğiz. Kendi yasal yükümlülüklerimizi yerine getirmek için belirli kişisel verileri saklamamızın yasal olarak gerekli olabileceğini lütfen unutmayın.`
      },
      section5: {
        title: "5. Haklarınız",
        text: "GDPR kapsamında ilgili kişi olarak aşağıdaki haklara sahipsiniz:",
        items: [
          "Erişim Hakkı: Hakkınızda topladığımız kişisel verilere erişim ve bunların bir kopyasını alma hakkına sahipsiniz.",
          "Düzeltme Hakkı: Hatalı veya eksik kişisel verilerin düzeltilmesini talep etme hakkına sahipsiniz.",
          "Silme Hakkı: Belirli koşullar altında kişisel verilerinizin silinmesini talep etme hakkına sahipsiniz.",
          "İşlemenin Kısıtlanması Hakkı: Belirli koşullar altında kişisel verilerinizin işlenmesinin kısıtlanmasını talep etme hakkına sahipsiniz.",
          "Veri Taşınabilirliği Hakkı: Hakkınızda toplanan kişisel verileri yapılandırılmış, yaygın olarak kullanılan ve makine tarafından okunabilir bir formatta alma hakkına sahipsiniz.",
          "İtiraz Hakkı: Belirli koşullar altında kişisel verilerinizin işlenmesine itiraz etme hakkına sahipsiniz.",
          "Onayı Geri Çekme Hakkı: Kişisel verilerinizin işlenmesi için onayınıza güveniyorsak, bu onayı istediğiniz zaman geri çekme hakkına sahipsiniz."
        ]
      },
      section6: {
        title: "6. İletişim",
        text: "Kişisel verilerinizi nasıl kullandığımız hakkında sorularınız veya yorumlarınız varsa, istediğiniz zaman info@turkishemarket.com adresinden Veri Koruma Görevlimize e-posta gönderebilirsiniz."
      },
      section7: {
        title: "7. Veri Koruma Otoritesine Şikayette Bulunma Hakkı",
        text: "Talebinize veya sorunuza yeterince yanıt vermediğimizi düşünüyorsanız, ikamet ettiğiniz ülkedeki yetkili denetim otoritesine şikayette bulunma hakkına sahipsiniz."
      }
    },
    en: {
      title: "DATA PROTECTION NOTICE",
      effectiveDate: "Effective from: January 10, 2026",
      intro: {
        title: "Introduction",
        text: `We are committed to protecting your privacy and personal data in accordance with the European Union General Data Protection Regulation ("GDPR") and other applicable data protection laws. This Data Protection Notice explains how we collect, use, share, and protect your personal data when you use our service for storing your credit/debit card information on the platform (mobile app or website on mobile/desktop device).

Storing your credit/debit card information is voluntary. With your consent, we store it in our system to improve your shopping experience and allow you to reuse it for future purchases and payments. You can also shop without registering your card, and whether you want to save your card information is entirely up to you. You can delete your saved card information from the platform at any time.`
      },
      controller: {
        title: "DATA CONTROLLER",
        text: `E-Market Germany UG (or "Company") is the data controller for personal data collected and used in the EEA, United Kingdom, and Switzerland.`,
        contact: `E-Market Germany UG
Cologne Trade Registry HRB 106542
Thessaloniki Allee 12, 51103 Cologne, Germany`
      },
      section1: {
        title: "1. What Data Does E-Market Germany UG (Representative of Turkishemarket.com) Collect",
        text: `Card Information: When you save your card information by checking the card storage checkbox or by selecting the corresponding option and giving your consent, we collect your card details, specifically the first 6 and last 4 digits, expiration date, issuing bank, card system information, and the date the card was registered in the system.

E-Market Germany UG (representative of Turkishemarket.com) processes the above-mentioned personal data only on the basis of explicit consent.

To ensure data security, your card information is stored by the companies in accordance with PCI DSS Level 1 requirements.

Please note that your saved card will be linked to the phone number you provided with your order. To change the phone number associated with this credit/debit card, you must delete your registered card and add it again with the desired phone number. When you save your card on the platform, you can use it for purchases on the platform in different countries.`
      },
      section2: {
        title: "2. How Does E-Market Germany UG (Representative of Turkishemarket.com) Use Your Data?",
        text: "The companies process your data for the following purposes:",
        items: [
          "To improve your shopping experience",
          "To optimize your payment process",
          "To allow you to reuse them for future purchases and payments",
          "To follow up on inquiries and complaints",
          "As evidence in any future disputes"
        ]
      },
      section3: {
        title: "3. With Whom Does E-Market Germany UG (Representative of Turkishemarket.com) Share Data?",
        text: "The companies do not, in principle, share card data with third parties. However, upon request or in the event of a possible legal dispute, they may be transferred to authorized institutions and organizations or judicial authorities in accordance with legal provisions to fulfill our own legal obligations."
      },
      section4: {
        title: "4. Data Retention",
        text: `We collect and use personal data for the purposes listed above. Depending on the purpose, the requirements and retention periods differ. We delete your personal data as soon as it is no longer needed for the purpose for which it was collected.

Requests for deletion of personal data are usually processed within 30 days. Under certain conditions, it may take longer; in this case, we will inform you accordingly. Please note that we may be legally required to retain certain personal data to meet our own legal obligations.`
      },
      section5: {
        title: "5. Your Rights",
        text: "As a data subject, you have the following rights under the GDPR:",
        items: [
          "Right of Access: You have the right to access the personal data we have collected about you and to receive a copy of it.",
          "Right to Rectification: You have the right to have inaccurate or incomplete personal data corrected.",
          "Right to Erasure: You have the right to request the deletion of your personal data under certain conditions.",
          "Right to Restriction of Processing: You have the right to request the restriction of processing of your personal data under certain conditions.",
          "Right to Data Portability: You have the right to receive the personal data collected about you in a structured, commonly used, and machine-readable format.",
          "Right to Object: You have the right to object to the processing of your personal data under certain conditions.",
          "Right to Withdraw Consent: If we rely on your consent to process your personal data, you have the right to withdraw that consent at any time."
        ]
      },
      section6: {
        title: "6. Contact",
        text: "If you have any questions or comments about how we use your personal data, you can contact our Data Protection Officer at any time by email at info@turkishemarket.com."
      },
      section7: {
        title: "7. Right to Lodge a Complaint with a Data Protection Authority",
        text: "If you believe that we have not adequately responded to your request or question, you have the right to lodge a complaint with the supervisory authority in your country of residence."
      }
    },
    fr: {
      title: "AVIS DE PROTECTION DES DONNÉES",
      effectiveDate: "En vigueur à partir du: 10 janvier 2026",
      intro: {
        title: "Introduction",
        text: `Nous nous engageons à protéger votre vie privée et vos données personnelles conformément au Règlement général sur la protection des données de l'Union européenne ("RGPD") et aux autres lois applicables en matière de protection des données. Cet avis de protection des données explique comment nous collectons, utilisons, partageons et protégeons vos données personnelles lorsque vous utilisez notre service de stockage de vos informations de carte de crédit/débit sur la plateforme.

Le stockage de vos informations de carte de crédit/débit est volontaire. Avec votre consentement, nous les stockons dans notre système pour améliorer votre expérience d'achat et vous permettre de les réutiliser pour de futurs achats et paiements.`
      },
      controller: {
        title: "RESPONSABLE DU TRAITEMENT",
        text: `E-Market Germany UG (ou "Société") est le responsable du traitement des données personnelles collectées et utilisées dans l'EEE, au Royaume-Uni et en Suisse.`,
        contact: `E-Market Germany UG
Registre du commerce de Cologne HRB 106542
Thessaloniki Allee 12, 51103 Cologne, Allemagne`
      },
      section1: {
        title: "1. Quelles données E-Market Germany UG (représentant de Turkishemarket.com) collecte-t-il",
        text: `Informations de carte: Lorsque vous enregistrez vos informations de carte, nous collectons vos données de carte, notamment les 6 premiers et les 4 derniers chiffres, la date d'expiration, la banque émettrice et les informations sur le système de carte.`
      },
      section2: {
        title: "2. Comment E-Market Germany UG utilise-t-il vos données?",
        text: "Les entreprises traitent vos données aux fins suivantes:",
        items: [
          "Pour améliorer votre expérience d'achat",
          "Pour optimiser votre processus de paiement",
          "Pour vous permettre de les réutiliser pour de futurs achats",
          "Pour donner suite aux demandes et réclamations",
          "Comme preuve dans d'éventuels litiges futurs"
        ]
      },
      section3: {
        title: "3. Avec qui E-Market Germany UG partage-t-il les données?",
        text: "Les entreprises ne partagent pas, en principe, les données de carte avec des tiers."
      },
      section4: {
        title: "4. Conservation des données",
        text: "Nous collectons et utilisons les données personnelles aux fins énumérées ci-dessus. Nous supprimons vos données personnelles dès qu'elles ne sont plus nécessaires."
      },
      section5: {
        title: "5. Vos droits",
        text: "En tant que personne concernée, vous disposez des droits suivants en vertu du RGPD:",
        items: [
          "Droit d'accès",
          "Droit de rectification",
          "Droit à l'effacement",
          "Droit à la limitation du traitement",
          "Droit à la portabilité des données",
          "Droit d'opposition",
          "Droit de retirer le consentement"
        ]
      },
      section6: {
        title: "6. Contact",
        text: "Si vous avez des questions, vous pouvez contacter notre délégué à la protection des données à info@turkishemarket.com."
      },
      section7: {
        title: "7. Droit de déposer une plainte",
        text: "Vous avez le droit de déposer une plainte auprès de l'autorité de contrôle de votre pays de résidence."
      }
    }
  };

  // Get content for current language, fallback to English
  const c = content[currentLang] || content['en'] || content['de'];

  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      <PageSeo title={c.title} description={c.intro?.text} segments={['privacy']} />
      {/* Header */}
      <div className="bg-primary/5 border-b">
        <div className="container mx-auto px-4 md:px-8 py-8">
          <Button 
            variant="ghost" 
            className="mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold">{c.title}</h1>
              <div className="flex items-center gap-2 text-muted-foreground mt-1">
                <Calendar className="w-4 h-4" />
                <span>{c.effectiveDate}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 py-12 max-w-4xl">
        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-primary" />
              {c.intro.title}
            </h2>
            <p className="text-muted-foreground whitespace-pre-line">{c.intro.text}</p>
          </section>

          {/* Data Controller */}
          <section className="mb-10 bg-secondary/30 rounded-xl p-6">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-primary" />
              {c.controller.title}
            </h2>
            <p className="text-muted-foreground mb-4">{c.controller.text}</p>
            <div className="bg-background rounded-lg p-4 border">
              <p className="whitespace-pre-line font-mono text-sm">{c.controller.contact}</p>
            </div>
          </section>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-primary" />
              {c.section1.title}
            </h2>
            <p className="text-muted-foreground whitespace-pre-line">{c.section1.text}</p>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-primary" />
              {c.section2.title}
            </h2>
            <p className="text-muted-foreground mb-4">{c.section2.text}</p>
            <ul className="list-disc pl-6 space-y-2">
              {c.section2.items.map((item, i) => (
                <li key={i} className="text-muted-foreground">{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">{c.section3.title}</h2>
            <p className="text-muted-foreground">{c.section3.text}</p>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">{c.section4.title}</h2>
            <p className="text-muted-foreground whitespace-pre-line">{c.section4.text}</p>
          </section>

          {/* Section 5 - Rights */}
          <section className="mb-10 bg-blue-50/50 rounded-xl p-6">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-primary" />
              {c.section5.title}
            </h2>
            <p className="text-muted-foreground mb-4">{c.section5.text}</p>
            <ul className="space-y-3">
              {c.section5.items.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-primary">{i + 1}</span>
                  </span>
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 6 - Contact */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-primary" />
              {c.section6.title}
            </h2>
            <p className="text-muted-foreground">{c.section6.text}</p>
            <a 
              href="mailto:info@turkishemarket.com" 
              className="inline-flex items-center gap-2 mt-3 text-primary hover:underline"
            >
              <Mail className="w-4 h-4" />
              info@turkishemarket.com
            </a>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">{c.section7.title}</h2>
            <p className="text-muted-foreground">{c.section7.text}</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
